import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, MapPinned, Pencil, Plus, Search, X } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { ActivitySite, PakistanCity } from '../../types/domain';
import { ActivitySiteImportPanel } from './ActivitySiteImportPanel';

interface Draft {
  kind: ActivitySite['kind'];
  name: string;
  campus_name: string;
  address: string;
  area: string;
  city: string;
  province: string;
  latitude: string;
  longitude: string;
  coordinate_pair: string;
  is_active: boolean;
}

const emptyDraft: Draft = {
  kind: 'university', name: '', campus_name: '', address: '', area: '', city: '',
  province: '', latitude: '', longitude: '', coordinate_pair: '', is_active: true,
};

const siteTypeOptions: Array<{ value: ActivitySite['kind']; label: string }> = [
  { value: 'university', label: 'University' },
  { value: 'college', label: 'College' },
  { value: 'school', label: 'School' },
  { value: 'hospital_clinic', label: 'Hospital or clinic' },
  { value: 'corporate_office', label: 'Corporate office' },
  { value: 'mall_activation_area', label: 'Mall activation area' },
  { value: 'event_venue', label: 'Exhibition or event venue' },
  { value: 'community_centre', label: 'Community centre' },
  { value: 'outdoor_public_space', label: 'Outdoor or public space' },
  { value: 'other', label: 'Other activity site' },
];

function siteTypeLabel(kind: ActivitySite['kind']) {
  return siteTypeOptions.find((option) => option.value === kind)?.label ?? kind;
}

const coordinatePairPattern = String.raw`\s*-?\d{1,2}(?:\.\d{1,7})?\s*,\s*-?\d{1,3}(?:\.\d{1,7})?\s*`;

function combinedCoordinates(latitude: string, longitude: string) {
  if (!latitude && !longitude) return '';
  return `${latitude},${longitude}`;
}

function withCoordinatePair(draft: Draft, value: string): Draft {
  const match = value.match(/^\s*(-?\d{1,2}(?:\.\d{1,7})?)\s*,\s*(-?\d{1,3}(?:\.\d{1,7})?)\s*$/);
  if (!match) return { ...draft, coordinate_pair: value };
  return {
    ...draft,
    coordinate_pair: value,
    latitude: match[1] ?? '',
    longitude: match[2] ?? '',
  };
}

export function ActivitySitesPage() {
  const { accessToken, capabilities, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const canManage = capabilities.includes('venues:manage');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<ActivitySite | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const params = new URLSearchParams({ page: String(page), page_size: '20', ordering: 'city,name,campus_name' });
  if (search.trim()) params.set('search', search.trim());
  const sites = useApiCollection<ActivitySite>('activity-sites', `/activity-sites/?${params.toString()}`, accessToken);
  const cities = useApiCollection<PakistanCity>('activity-site-cities', '/pakistan-cities/?page_size=500&ordering=province,name', accessToken);
  const rows = sites.data?.results ?? [];

  const mutation = useMutation({
    mutationFn: () => apiRequest<ActivitySite>(editing === 'new' ? '/activity-sites/' : `/activity-sites/${editing?.id}/`, {
      ...(accessToken ? { accessToken } : {}),
      method: editing === 'new' ? 'POST' : 'PATCH',
      body: JSON.stringify({
        kind: draft.kind,
        name: draft.name,
        campus_name: draft.campus_name,
        address: draft.address,
        area: draft.area,
        city: draft.city,
        province: draft.province,
        country: 'Pakistan',
        latitude: draft.latitude,
        longitude: draft.longitude,
        timezone: 'Asia/Karachi',
        is_active: draft.is_active,
      }),
    }),
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'activity-sites'] });
    },
  });

  function beginCreate() {
    mutation.reset();
    setDraft(emptyDraft);
    setEditing('new');
  }

  function beginEdit(site: ActivitySite) {
    mutation.reset();
    setDraft({
      kind: site.kind, name: site.name, campus_name: site.campus_name, address: site.address,
      area: site.area, city: site.city, province: site.province, latitude: site.latitude,
      longitude: site.longitude, coordinate_pair: combinedCoordinates(site.latitude, site.longitude),
      is_active: site.is_active,
    });
    setEditing(site);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return <>
    <header className="page-heading">
      <div><p className="eyebrow">{selectedWorkspace?.name}</p><h1>Activity sites</h1><p>Reuse trusted universities, campuses, and other non-retail activation sites across campaigns.</p></div>
      <span className="record-count"><MapPinned size={16} /> {sites.data?.count ?? 0} sites</span>
    </header>
    <div className="catalogue-list-toolbar">
      <label className="search-field"><Search aria-hidden="true" size={17} /><input aria-label="Search activity sites" onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search institution, campus, address or city" type="search" value={search} /></label>
      {canManage ? <div className="page-heading-actions"><button className="button secondary" onClick={() => setImporting(true)} type="button"><FileSpreadsheet size={17} /> Bulk import</button><button className="button primary" onClick={beginCreate} type="button"><Plus size={17} /> Add activity site</button></div> : null}
    </div>
    {importing && canManage ? <ActivitySiteImportPanel accessToken={accessToken} onClose={() => setImporting(false)} /> : null}
    <DataPanel loading={sites.isLoading} error={sites.isError} empty={rows.length === 0}>
      <section className="content-card data-card"><div className="data-table-wrap"><table className="data-table">
        <thead><tr><th>Activity site</th><th>Address</th><th>Coordinates</th><th>Type</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>{rows.map((site) => <tr key={site.id}>
          <td><strong>{site.name}</strong><small>{site.campus_name || 'No campus name'}</small></td>
          <td>{site.address}<small>{site.area ? `${site.area} · ` : ''}{site.city}, {site.province || site.country}</small></td>
          <td className="muted-cell">{site.latitude}, {site.longitude}</td>
          <td>{siteTypeLabel(site.kind)}</td>
          <td><StatusPill value={site.is_active ? 'active' : 'inactive'} /></td>
          <td>{canManage ? <button className="text-button" onClick={() => beginEdit(site)} type="button"><Pencil size={14} /> Edit</button> : null}</td>
        </tr>)}</tbody>
      </table></div></section>
    </DataPanel>
    <div className="attendance-pagination"><button className="button secondary" disabled={page === 1} onClick={() => setPage((value) => value - 1)} type="button">Previous</button><span>Page {page} · {sites.data?.count ?? 0} records</span><button className="button secondary" disabled={!sites.data?.next} onClick={() => setPage((value) => value + 1)} type="button">Next</button></div>

    {editing ? <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setEditing(null); }} role="presentation">
      <section aria-labelledby="activity-site-catalogue-form-title" aria-modal="true" className="confirmation-dialog venue-form-dialog" role="dialog">
        <button aria-label="Close activity site form" className="icon-button confirmation-close" disabled={mutation.isPending} onClick={() => setEditing(null)} type="button"><X size={19} /></button>
        <div className="card-heading venue-form-dialog-heading"><div><h2 id="activity-site-catalogue-form-title">{editing === 'new' ? 'Add activity site' : `Edit ${editing.name}`}</h2><p>Contact people and attendance radii are configured separately for each campaign.</p></div></div>
        <form className="management-form" onSubmit={submit}>
          <div className="form-grid">
            <label>Site type<select value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value as Draft['kind'] })}>{siteTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <label>Activity site name<input maxLength={160} required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
            <label>Campus name<input maxLength={160} value={draft.campus_name} onChange={(event) => setDraft({ ...draft, campus_name: event.target.value })} /></label>
            <label>City<select required value={draft.city} onChange={(event) => { const city = (cities.data?.results ?? []).find((item) => item.name === event.target.value); setDraft({ ...draft, city: city?.name ?? '', province: city?.province ?? '' }); }}><option value="">{cities.isLoading ? 'Loading cities…' : cities.isError ? 'Unable to load cities' : 'Select city'}</option>{draft.city && !(cities.data?.results ?? []).some((item) => item.name === draft.city) ? <option value={draft.city}>{draft.city} (existing value)</option> : null}{(cities.data?.results ?? []).map((city) => <option key={city.id} value={city.name}>{city.name} · {city.province}</option>)}</select></label>
            <label>Area<input maxLength={120} value={draft.area} onChange={(event) => setDraft({ ...draft, area: event.target.value })} /></label>
            <label>Province<input readOnly value={draft.province} /></label>
            <label className="wide-field">Latitude, longitude (paste from Google Maps)<input inputMode="decimal" pattern={coordinatePairPattern} placeholder="31.5070059,74.3504354" value={draft.coordinate_pair} onChange={(event) => setDraft(withCoordinatePair(draft, event.target.value))} /></label>
            <label>Latitude<input max="90" min="-90" required step="0.0000001" type="number" value={draft.latitude} onChange={(event) => setDraft({ ...draft, latitude: event.target.value, coordinate_pair: combinedCoordinates(event.target.value, draft.longitude) })} /></label>
            <label>Longitude<input max="180" min="-180" required step="0.0000001" type="number" value={draft.longitude} onChange={(event) => setDraft({ ...draft, longitude: event.target.value, coordinate_pair: combinedCoordinates(draft.latitude, event.target.value) })} /></label>
            <label className="form-span-2">Address<textarea required rows={3} value={draft.address} onChange={(event) => setDraft({ ...draft, address: event.target.value })} /></label>
            <label className="checkbox-field form-span-2"><input checked={draft.is_active} type="checkbox" onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Available for new campaigns</label>
          </div>
          {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
          <div className="form-actions"><button className="button secondary" disabled={mutation.isPending} onClick={() => setEditing(null)} type="button">Cancel</button><button className="button primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : null}{editing === 'new' ? 'Add activity site' : 'Save activity site'}</button></div>
        </form>
      </section>
    </div> : null}
  </>;
}
